import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { postData } from "../../../../utility/Utility";
import CustomLoader from "../../../../Components/CustomLoader/CustomLoader";

function TestimonialList() {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const rowsPerPage = 10;

  const fetchTestimonials = async (page = 1, search = "") => {
    setLoading(true);
    try {
      const response = await postData("api/testimonials/get-all", {
        page: page,
        limit: rowsPerPage,
        search: search
      });
      
      if (response?.success) {
        setTestimonials(response.testimonials || []);
        // Calculate current page and total pages based on count
        setTotalPages(Math.ceil((response.count || 0) / rowsPerPage));
        setCurrentPage(page - 1);
      } else {
        toast.error(response?.message || "Failed to fetch testimonials");
      }
    } catch (error) {
      toast.error("Error fetching testimonials");
      console.error("Fetch testimonials error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials(currentPage + 1, searchTerm);
  }, [currentPage, searchTerm]);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchTestimonials(1, searchTerm);
  };

  const handleReset = () => {
    setSearchTerm("");
    setCurrentPage(0);
    fetchTestimonials(1);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await postData(`api/testimonials/delete/${id}`, {});
        
        if (response.success) {
          toast.success("Testimonial deleted successfully");
          fetchTestimonials(currentPage + 1, searchTerm);
        } else {
          toast.error(response.message || "Failed to delete testimonial");
        }
      } catch (error) {
        toast.error("Error deleting testimonial");
        console.error("Delete testimonial error:", error);
      }
    }
  };

  const handleStatusChange = async (testimonial) => {
    const newStatus = testimonial.status === "active" ? "inactive" : "active";

    const result = await Swal.fire({
      title: `Are you sure you want to ${newStatus === "active" ? "activate" : "deactivate"} this testimonial?`,
      text: `The testimonial will be ${newStatus === "active" ? "activated" : "deactivated"}.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${newStatus === "active" ? "activate" : "deactivate"} it!`,
    });

    if (result.isConfirmed) {
      try {
        // Create FormData for the update
        const formData = new FormData();
        formData.append('id', testimonial._id);
        formData.append('title', testimonial.title);
        formData.append('content', testimonial.content);
        formData.append('status', newStatus);
        
        // If there's an image, append it (as string URL, not as file)
        if (testimonial.image) {
          formData.append('image', testimonial.image);
        }
        
        // If activating this testimonial, ensure all others are deactivated
        if (newStatus === "active") {
          // This will be handled on the server side, but we can prepare the UI
          // by updating local state to reflect that only one can be active
          setTestimonials(prevTestimonials => 
            prevTestimonials.map(item => ({
              ...item,
              status: item._id === testimonial._id ? 'active' : 'inactive'
            }))
          );
        }
        
        const response = await postData(`api/testimonials/update/${testimonial._id}`, formData);
        
        if (response.success) {
          toast.success(`Testimonial ${newStatus === "active" ? "activated" : "deactivated"} successfully`);
          // Refresh the list to get the updated data from server
          fetchTestimonials(currentPage + 1, searchTerm);
        } else {
          toast.error(response.message || "Failed to update testimonial status");
          // Revert the optimistic UI update if the server request failed
          fetchTestimonials(currentPage + 1, searchTerm);
        }
      } catch (error) {
        toast.error(`Error ${newStatus === "active" ? "activating" : "deactivating"} testimonial`);
        console.error("Status change error:", error);
        // Revert the optimistic UI update if there was an error
        fetchTestimonials(currentPage + 1, searchTerm);
      }
    }
  };


  const pageCount = totalPages;
  const offset = currentPage * rowsPerPage;
  const currentItems = testimonials;

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <h4 className="fs-18 fw-semibold m-0">Testimonial Management</h4>
              <p className="fw-semibold my-2">Testimonial List</p>
            </div>
            <div>
              <button
                className="btn btn-primary"
                onClick={() => navigate("/add-testimonial")}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm me-1"></span>
                ) : (
                  <i className="mdi mdi-plus-square me-1"></i>
                )}
                Add Testimonial
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3 align-items-center">
                      <div className="col-md-12 col-lg-4">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control ps-4"
                            placeholder="Search testimonials..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <span className="input-group-text">
                            <i className="mdi mdi-magnify"></i>
                          </span>
                        </div>
                      </div>

                      <div className="col-md-12 col-lg-3 d-flex gap-2">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? (
                            <span className="spinner-border spinner-border-sm me-1"></span>
                          ) : null}
                          Search
                        </button>

                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleReset}
                          disabled={loading}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          <div className="row mt-2">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Image</th>
                            <th>Title</th>
                            <th>Content</th>
                            <th>Status</th>
                            <th>Created At</th>
                            <th width="150px">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.length > 0 ? (
                            currentItems.map((testimonial, index) => (
                              <tr key={testimonial._id}>
                                <td>{offset + index + 1}</td>
                                <td>
                                  {testimonial.image ? (
                                    <img 
                                      src={testimonial.image} 
                                      alt={testimonial.title}
                                      className="img-thumbnail" 
                                      style={{ maxHeight: '50px', maxWidth: '50px' }} 
                                    />
                                  ) : (
                                    <div className="text-center" style={{ width: '50px', height: '50px', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}>
                                      <i className="mdi mdi-image-outline text-muted" style={{ fontSize: '24px' }}></i>
                                    </div>
                                  )}
                                </td>
                                <td>{testimonial.title}</td>
                                <td>{testimonial.content}</td>
                                <td>
                                  <span
                                    className={`badge text-capitalize bg-${testimonial.status === 'active' ? 'success' : 'danger'}`}
                                  >
                                    {testimonial.status}
                                  </span>
                                </td>
                                <td>
                                  {new Date(testimonial.createdAt).toLocaleDateString()}
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() =>
                                        navigate(`/update-testimonial`, {
                                          state: { testimonialData: testimonial },
                                        })
                                      }
                                    >
                                      <i className="mdi mdi-pencil"></i>
                                    </button>
                                    <button
                                      className={`btn btn-sm btn-outline-${testimonial.status === 'active' ? 'warning' : 'success'}`}
                                      onClick={() => handleStatusChange(testimonial)}
                                      title={testimonial.status === 'active' ? 'Deactivate' : 'Activate'}
                                    >
                                      <i className={`mdi mdi-${testimonial.status === 'active' ? 'close' : 'check'}`}></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleDelete(testimonial._id)}
                                    >
                                      <i className="mdi mdi-delete"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center py-4">
                                No testimonials found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {pageCount > 1 && (
                        <ReactPaginate
                          previousLabel={<i className="mdi mdi-chevron-left"></i>}
                          nextLabel={<i className="mdi mdi-chevron-right"></i>}
                          breakLabel="..."
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          pageCount={pageCount}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName="pagination pagination-rounded justify-content-end mt-3"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TestimonialList;